import { fetchOrderStatistics } from '@/services/supplierOrders'
import { FTWebSocket } from '@/utils/FTWebSocket'

/**
 * Mutation types of the store
 */
const mutationTypes = {
  FETCHING: 'FETCHING',
  FETCHED: 'FETCHED',
  SET_COUNTERS: 'SET_COUNTERS',
  UPDATE_COUNTERS: 'UPDATE_COUNTERS',
  UPDATE_DRAFT_COUNT: 'UPDATE_DRAFT_COUNT',
  UPDATE_AWAITING_DELIVERY_COUNT: 'UPDATE_AWAITING_DELIVERY_COUNT',
  UPDATE_AWAITING_DESPATCH_COUNT: 'UPDATE_AWAITING_DESPATCH_COUNT'
}

/**
 * Initial state
 */
const state = {
  loading: false,
  fetching: false,
  counters: {
    draftCount: 0,
    awaitingDeliveryCount: 0,
    awaitingDespatchCount: 0
  }
}

const nonNegativeCount = (count) => Math.max(Number(count) || 0, 0)

/**
 * Getters
 */
const getters = {
  liveOrdersTotal(state) {
    return (
      state.counters.draftCount +
      state.counters.awaitingDeliveryCount +
      state.counters.awaitingDespatchCount
    )
  }
}

/**
 * Actions
 */
const actions = {
  /**
   * @desc Fetch total of live orders
   */
  fetchOrderStatistics({ commit }) {
    commit(mutationTypes.FETCHING)
    return fetchOrderStatistics().then((response) => {
      commit(mutationTypes.FETCHED)
      commit(mutationTypes.SET_COUNTERS, response)
    })
  },

  updateDraftCount({ commit }, draftCount) {
    commit(mutationTypes.UPDATE_DRAFT_COUNT, draftCount)
  },

  updateAwaitingDeliveryCount({ commit }, awaitingDeliveryCount) {
    commit(mutationTypes.UPDATE_AWAITING_DELIVERY_COUNT, awaitingDeliveryCount)
  },

  updateAwaitingDespatchCount({ commit }, awaitingDespatchCount) {
    commit(mutationTypes.UPDATE_AWAITING_DESPATCH_COUNT, awaitingDespatchCount)
  },

  subscribeToLiveOrders({ commit, dispatch, rootState }) {
    return FTWebSocket.subscribe(
      '/user/queue/purchase-orders/new',
      (counters) => {
        const selectedSupplierId = rootState?.preferences?.selectedSupplierId
        // websocket sends events for all suppliers linked to user account
        // we need to only update counter if event relates to selected supplier
        if (counters.supplierId === selectedSupplierId) {
          console.log(
            'Received purchase order websocket notification',
            counters
          )
          commit(mutationTypes.UPDATE_COUNTERS, counters)

          let text
          const updatedItem = counters.updatedItems?.[0]?.supplierOrderId || ''

          if (counters?.newDraftCount === 1)
            text = `Purchase Order ${updatedItem} has been received.`
          else if (counters?.newAwaitingDespatchCount === 1)
            text = `Purchase Order ${updatedItem} has been updated to IN TRANSIT.`
          else if (counters?.newAwaitingDeliveryCount === 1)
            text = `Purchase Order ${updatedItem} has been updated to DELIVERED.`
          else if (counters?.newAwaitingDeliveryCount === -1)
            text = `Purchase Order ${updatedItem} has been DELIVERED.`

          dispatch(
            'notifications/addNotification',
            {
              color: 'warning',
              closable: true,
              text,
              timeout: -1
            },
            { root: true }
          )
        }
      }
    )
  }
}

/**
 * Mutations
 */
const mutations = {
  [mutationTypes.FETCHING](state) {
    state.fetching = true
    state.loading = true
  },

  [mutationTypes.FETCHED](state) {
    state.fetching = false
    state.loading = false
  },

  [mutationTypes.SET_COUNTERS](state, counters) {
    state.counters = {
      draftCount: nonNegativeCount(counters.draftCount),
      awaitingDeliveryCount: nonNegativeCount(counters.awaitingDeliveryCount),
      awaitingDespatchCount: nonNegativeCount(counters.awaitingDespatchCount)
    }
  },

  [mutationTypes.UPDATE_COUNTERS](state, counters) {
    state.counters = {
      draftCount: nonNegativeCount(
        state.counters.draftCount + counters.newDraftCount
      ),
      awaitingDeliveryCount: nonNegativeCount(
        state.counters.awaitingDeliveryCount + counters.newAwaitingDeliveryCount
      ),
      awaitingDespatchCount: nonNegativeCount(
        state.counters.awaitingDespatchCount + counters.newAwaitingDespatchCount
      )
    }
  },

  [mutationTypes.UPDATE_DRAFT_COUNT](state, draftCount) {
    state.counters.draftCount = nonNegativeCount(draftCount)
  },

  [mutationTypes.UPDATE_AWAITING_DELIVERY_COUNT](state, awaitingDeliveryCount) {
    state.counters.awaitingDeliveryCount = nonNegativeCount(
      awaitingDeliveryCount
    )
  },

  [mutationTypes.UPDATE_AWAITING_DESPATCH_COUNT](state, awaitingDespatchCount) {
    state.counters.awaitingDespatchCount = nonNegativeCount(
      awaitingDespatchCount
    )
  }
}

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state
}
